import { Link } from 'react-router';
import { usePlans } from '../api/hooks';
import { PublicFooter, PublicNav } from '../components/PublicLayout';

const FEATURES = [
  [
    'Comment to DM',
    'Turn every comment on a post or reel into a personal DM with tracked links, buttons and images.',
  ],
  [
    'Story replies',
    'Capture everyone who replies to your story as a contact and answer them instantly.',
  ],
  [
    'Keyword triggers',
    'Exact, contains, any-of and all-of keyword rules with round-robin public replies.',
  ],
  [
    'Visual flows',
    'Multi-step conversations that collect name, email and phone, branch on answers and hand off to a human.',
  ],
  [
    'Inbox and CRM',
    'Every conversation and contact in one place, with tags, custom fields and attribution.',
  ],
  [
    'Analytics and API',
    'Delivery per post, story and automation on every plan; clicks, conversions, webhooks and a public API from Legend.',
  ],
] as const;

/** UI-71: the reference landing's strip, quotes and FAQ; the quotes are sample copy, not customers. */
const STEPS = [
  [
    '1. Connect',
    'Link your Instagram professional account in one click; AutoDM asks only for the permissions it needs.',
  ],
  [
    '2. Set a rule',
    'Pick a post or story, choose a keyword, write the DM; or start from a flow template.',
  ],
  [
    '3. Watch the DMs go',
    'Every matching comment gets a reply and a DM, every lead lands in your contacts, every click is counted.',
  ],
] as const;
const QUOTES = [
  ['"Our link-in-bio DMs went out in seconds instead of hours."', 'Creator, 120k followers'],
  ['"The flow collected emails from a launch post while we slept."', 'D2C brand'],
  ['"Delivery per post told us which reel actually sold."', 'Agency account manager'],
] as const;
const FAQ = [
  [
    'Does AutoDM need my Instagram password?',
    'No. You authorize AutoDM through Instagram; it never sees your password and you can revoke access any time.',
  ],
  [
    'Which accounts can connect?',
    'Instagram professional accounts (business or creator). Personal accounts cannot receive automated DMs under the platform rules.',
  ],
  [
    'What happens when the trial ends?',
    'Automations pause until you pick a plan; nothing is deleted, and your contacts and history stay.',
  ],
  [
    'Can my team use it?',
    'Yes. Invite members as owner, manager, operator or developer, or build a custom role with exactly the permissions you want.',
  ],
  [
    'Is there an API?',
    'From the Legend plan: API keys, webhooks for comments, DMs and leads, and an OpenAPI document you can import into any client.',
  ],
] as const;

export function LandingPage() {
  const plans = usePlans();
  return (
    <div className="landing">
      <PublicNav />
      <section className="hero">
        <h1>Turn Instagram comments into customers, automatically</h1>
        <p>
          Connect your professional account, pick a post, set a keyword, and let AutoDM reply, DM,
          collect leads and report on every step.
        </p>
        <div className="actions" style={{ justifyContent: 'center' }}>
          <Link className="btn btn-primary" to="/signup">
            Get started, 7 days free
          </Link>
          <a className="btn" href="#pricing">
            See pricing
          </a>
        </div>
      </section>
      <section className="features grid grid-3">
        {FEATURES.map(([title, text]) => (
          <div className="card" key={title}>
            <h3>{title}</h3>
            <p className="muted">{text}</p>
          </div>
        ))}
      </section>
      <section className="how" id="how">
        <h2 style={{ textAlign: 'center', marginBottom: 16 }}>How it works</h2>
        <div className="grid grid-3">
          {STEPS.map(([title, text]) => (
            <div className="card" key={title}>
              <h3>{title}</h3>
              <p className="muted">{text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="quotes">
        <h2 style={{ textAlign: 'center', marginBottom: 4 }}>What creators say</h2>
        <p className="muted small" style={{ textAlign: 'center', marginBottom: 16 }}>
          Sample copy: these quotes illustrate the product and are not from customers.
        </p>
        <div className="grid grid-3">
          {QUOTES.map(([quote, who]) => (
            <blockquote className="card" key={who}>
              <p>{quote}</p>
              <cite className="muted small">{who}</cite>
            </blockquote>
          ))}
        </div>
      </section>
      <section className="pricing" id="pricing">
        <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Simple pricing</h2>
        <div className="grid grid-4">
          {(plans.data?.items ?? []).map((p) => (
            <div className={`card plan-card ${p.plan === 'legend' ? 'featured' : ''}`} key={p.plan}>
              <div className="card-title">{p.name}</div>
              {p.plan === 'trial' ? (
                <div className="price">
                  Free<small> for 7 days</small>
                </div>
              ) : (
                <>
                  <div className="price">
                    ${p.yearlyUsdPerMonth}
                    <small>/month billed yearly</small>
                  </div>
                  <div className="muted small">or ${p.monthlyUsd}/month</div>
                </>
              )}
              <ul className="features-list">
                {p.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              <Link className="btn btn-primary" to="/signup" style={{ marginTop: 14 }}>
                Choose {p.name}
              </Link>
            </div>
          ))}
        </div>
      </section>
      <section className="faq" id="faq">
        <h2 style={{ textAlign: 'center', marginBottom: 16 }}>Questions</h2>
        {FAQ.map(([q, a]) => (
          <details className="card" key={q}>
            <summary>{q}</summary>
            <p className="muted">{a}</p>
          </details>
        ))}
      </section>
      <PublicFooter />
    </div>
  );
}
